// camera-permission.service.ts - FIXED: Browser popup keeps retrying when native granted
import { Injectable, NgZone } from '@angular/core';
import { Platform } from '@ionic/angular';
import { BehaviorSubject } from 'rxjs';
import { PopupService } from 'src/modules/shared/services/popup.service';
import { AndroidPermissions } from '@ionic-native/android-permissions/ngx';
import { Diagnostic } from '@ionic-native/diagnostic/ngx';
import { Device } from '@ionic-native/device/ngx';

interface PermissionAttempt {
  timestamp: number;
  wasGranted: boolean;
  type: 'NATIVE' | 'BROWSER'; // NEW: Track type
}

interface PermissionCheckResult {
  camera: boolean;
  media?: boolean;
}

interface RequestPermissionResult {
  granted: boolean;
  showSettings: boolean;
}

@Injectable({ providedIn: 'root' })
export class CameraPermissionService {
  private permissionGranted$ = new BehaviorSubject<boolean>(false);
  
  private isChecking = false;
  private isRequestingPermission = false;
  private lastCheckTime = 0;
  
  // Track ALL attempts with type
  private permissionAttempts: PermissionAttempt[] = [];
  private readonly MAX_NATIVE_ATTEMPTS_BEFORE_SETTINGS = 1; // Native denials only
  private readonly ATTEMPT_WINDOW_MS = 5 * 60 * 1000; // 5 minutes
  
  private readonly DEBOUNCE_MS = 2000;

  private isIOS: boolean = false;
  private isAndroid: boolean = false;
  private isCordova: boolean = false;

  constructor(
    private platform: Platform,
    private popupService: PopupService,
    private diagnostic: Diagnostic,
    private device: Device,
    private androidPermissions: AndroidPermissions,
    private ngZone: NgZone
  ) {
    this.isIOS = this.platform.is('ios');
    this.isAndroid = this.platform.is('android');
    this.isCordova = this.platform.is('cordova');
    
    console.log('[CameraPermission] Platform detected:', {
      ios: this.isIOS,
      android: this.isAndroid,
      cordova: this.isCordova
    });
    
    this.loadPersistedAttempts();
  }

  // ==================== STORAGE ====================

  private loadPersistedAttempts(): void {
    try {
      const stored = localStorage.getItem('camera_permission_attempts');
      if (stored) {
        const attempts = JSON.parse(stored) as PermissionAttempt[];
        const cutoff = Date.now() - this.ATTEMPT_WINDOW_MS;
        this.permissionAttempts = attempts.filter(a => a.timestamp > cutoff);
        console.log('[CameraPermission] Loaded attempts:', {
          total: this.permissionAttempts.length,
          native: this.permissionAttempts.filter(a => a.type === 'NATIVE').length,
          browser: this.permissionAttempts.filter(a => a.type === 'BROWSER').length
        });
      }
    } catch (error) {
      console.error('[CameraPermission] Error loading attempts:', error);
      this.permissionAttempts = [];
    }
  }

  private persistAttempts(): void {
    try {
      localStorage.setItem('camera_permission_attempts', JSON.stringify(this.permissionAttempts));
    } catch (error) {
      console.error('[CameraPermission] Error persisting attempts:', error);
    }
  }

  private recordPermissionAttempt(wasGranted: boolean, type: 'NATIVE' | 'BROWSER'): void {
    const now = Date.now();
    const cutoff = now - this.ATTEMPT_WINDOW_MS;
    this.permissionAttempts = this.permissionAttempts.filter(a => a.timestamp > cutoff);
    
    this.permissionAttempts.push({
      timestamp: now,
      wasGranted,
      type
    });
    
    console.log('[CameraPermission] Recorded attempt:', {
      type,
      wasGranted,
      nativeDenials: this.getNativeDenialCount(),
      browserDenials: this.getBrowserDenialCount()
    });
    
    this.persistAttempts();
  }

  get permissionChanges() {
    return this.permissionGranted$.asObservable();
  }

  getNativeDenialCount(): number {
    const denials = this.permissionAttempts.filter(
      a => a.type === 'NATIVE' && !a.wasGranted
    );
    return denials.length;
  }

  getBrowserDenialCount(): number {
    const denials = this.permissionAttempts.filter(
      a => a.type === 'BROWSER' && !a.wasGranted
    );
    return denials.length;
  }

  shouldShowSettings(): boolean {
    // CRITICAL: Only consider NATIVE denials for settings popup
    // Browser denials should NEVER trigger settings popup
    const nativeDenialCount = this.getNativeDenialCount();
    const shouldShow = nativeDenialCount >= this.MAX_NATIVE_ATTEMPTS_BEFORE_SETTINGS;
    
    console.log('[CameraPermission] shouldShowSettings check:', {
      nativeDenialCount,
      browserDenialCount: this.getBrowserDenialCount(),
      threshold: this.MAX_NATIVE_ATTEMPTS_BEFORE_SETTINGS,
      shouldShow
    });
    
    return shouldShow;
  }

  clearAllAttempts(): void {
    console.log('[CameraPermission] Clearing all permission attempts');
    this.permissionAttempts = [];
    this.persistAttempts();
  }

  // ==================== PUBLIC API ====================

  async getAuthorizationStatus(): Promise<string> {
    console.log('[CameraPermission] getAuthorizationStatus called');
    
    try {
      if (this.isIOS && this.isCordova) {
        return await this.getIOSCameraStatus();
      }
      
      if (this.isAndroid && this.isCordova) {
        const hasPermission = await this.checkAndroidCameraPermission(false);
        return hasPermission ? 'GRANTED' : 'DENIED';
      }
      
      const hasPermission = await this.checkWebCameraPermission(false);
      return hasPermission ? 'GRANTED' : 'DENIED';
    } catch (error) {
      console.error('[CameraPermission] getAuthorizationStatus error:', error);
      return 'UNKNOWN';
    }
  }

  async requestPermissionIfNeeded(): Promise<RequestPermissionResult> {
    console.log('[CameraPermission] requestPermissionIfNeeded called');
    
    try {
      const granted = await this.checkCameraPermission(true);
      const showSettings = !granted && this.shouldShowSettings();
      
      return {
        granted,
        showSettings
      };
    } catch (error: any) {
      console.error('[CameraPermission] requestPermissionIfNeeded error:', error);
      
      return {
        granted: false,
        showSettings: false
      };
    }
  }

  async checkAndRequestPermissions(): Promise<PermissionCheckResult> {
    console.log('[CameraPermission] checkAndRequestPermissions called');
    try {
      return await this.checkAndRequestAll(true);
    } catch (error) {
      console.error('[CameraPermission] checkAndRequestPermissions error:', error);
      return { camera: false };
    }
  }

  // ==================== PERMISSION CHECK ====================

  private shouldDebounce(): boolean {
    const timeSinceLastCheck = Date.now() - this.lastCheckTime;
    return timeSinceLastCheck < this.DEBOUNCE_MS;
  }

  async checkCameraPermission(shouldRequest: boolean = false): Promise<boolean> {
    if (this.shouldDebounce()) {
      console.log('[CameraPermission] Debouncing, returning cached value:', this.permissionGranted$.value);
      return this.permissionGranted$.value;
    }

    if (this.isChecking) {
      console.log('[CameraPermission] Check already in progress, returning cached value');
      return this.permissionGranted$.value;
    }

    this.isChecking = true;
    this.lastCheckTime = Date.now();

    try {
      const result = await this.performCameraPermissionCheck(shouldRequest);
      this.permissionGranted$.next(result);
      return result;
    } catch (error: any) {
      console.error('[CameraPermission] Check failed:', error);
      this.permissionGranted$.next(false);
      return false;
    } finally {
      this.isChecking = false;
    }
  }

  private async performCameraPermissionCheck(shouldRequest: boolean): Promise<boolean> {
    console.log('[CameraPermission] Performing permission check', {
      shouldRequest,
      platform: { ios: this.isIOS, android: this.isAndroid, cordova: this.isCordova }
    });

    if (this.isIOS && this.isCordova) {
      return await this.checkIOSCameraPermission(shouldRequest);
    }

    if (this.isAndroid && this.isCordova) {
      return await this.checkAndroidCameraPermission(shouldRequest);
    }

    return await this.checkWebCameraPermission(shouldRequest);
  }

  // ==================== iOS PERMISSION ====================

  private async checkIOSCameraPermission(shouldRequest: boolean): Promise<boolean> {
    console.log('[CameraPermission] Checking iOS camera');
    
    try {
      // Step 1: Check native iOS permission
      const status = await this.diagnostic.getCameraAuthorizationStatus();
      console.log('[CameraPermission] Native iOS status:', status);
      
      // Step 2: If native granted, check WKWebView browser permission
      if (status === this.diagnostic.permissionStatus.GRANTED) {
        console.log('[CameraPermission] Native GRANTED, checking WKWebView...');
        return await this.checkWKWebViewPermission(shouldRequest);
      }
      
      // Step 3: Native not granted
      if (!shouldRequest) {
        this.recordPermissionAttempt(false, 'NATIVE');
        return false;
      }
      
      // Step 4: Request native permission if possible
      if (status === this.diagnostic.permissionStatus.NOT_REQUESTED || 
          status === this.diagnostic.permissionStatus.DENIED_ONCE) {
        console.log('[CameraPermission] Requesting native iOS permission...');
        const newStatus = await this.diagnostic.requestCameraAuthorization();
        
        if (newStatus === this.diagnostic.permissionStatus.GRANTED) {
          console.log('[CameraPermission] Native GRANTED, now checking WKWebView...');
          // Native granted, now check WKWebView
          return await this.checkWKWebViewPermission(true);
        }
        
        // Native denied
        console.log('[CameraPermission] Native permission DENIED');
        this.recordPermissionAttempt(false, 'NATIVE');
        return false;
      }
      
      // Permanently denied at native level
      console.log('[CameraPermission] Native permission permanently DENIED');
      this.recordPermissionAttempt(false, 'NATIVE');
      return false;
      
    } catch (error: any) {
      console.error('[CameraPermission] iOS permission error:', error);
      return false;
    }
  }

  private async checkWKWebViewPermission(shouldRequest: boolean): Promise<boolean> {
    console.log('[CameraPermission] Checking WKWebView, shouldRequest:', shouldRequest);
    
    try {
      // Always try getUserMedia - this triggers the browser permission popup
      // The popup will appear each time until user grants it
      console.log('[CameraPermission] Attempting getUserMedia for WKWebView...');
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      
      // Success!
      stream.getTracks().forEach(t => t.stop());
      console.log('[CameraPermission] ✅ WKWebView permission GRANTED');
      
      // Record success as BROWSER type
      this.recordPermissionAttempt(true, 'BROWSER');
      
      return true;
    } catch (webError: any) {
      console.error('[CameraPermission] WKWebView error:', webError.name);
      
      if (webError.name === 'NotAllowedError') {
        // User denied browser permission
        console.log('[CameraPermission] ❌ WKWebView permission DENIED by user');
        
        // Record as BROWSER denial (NOT native)
        // This will NOT trigger settings popup
        this.recordPermissionAttempt(false, 'BROWSER');
        
        console.log('[CameraPermission] Browser denial count:', this.getBrowserDenialCount());
      }
      
      return false;
    }
  }

  private async getIOSCameraStatus(): Promise<string> {
    try {
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const result = await navigator.permissions.query({ name: 'camera' as PermissionName });
          
          if (result.state === 'granted') return 'GRANTED';
          if (result.state === 'denied') return 'DENIED';
          return 'NOT_REQUESTED';
        } catch (permError) {
          console.warn('[CameraPermission] Permissions API not available');
        }
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(d => d.kind === 'videoinput');
      
      if (videoDevices.length > 0 && videoDevices[0].label) {
        return 'GRANTED';
      }
      
      return 'NOT_REQUESTED';
    } catch (error) {
      console.error('[CameraPermission] getIOSCameraStatus error:', error);
      return 'UNKNOWN';
    }
  }

  // ==================== ANDROID PERMISSION ====================

  private async checkAndroidCameraPermission(shouldRequest: boolean): Promise<boolean> {
    console.log('[CameraPermission] Android check, shouldRequest:', shouldRequest);

    try {
      const hasPermission = await this.androidPermissions.checkPermission(
        this.androidPermissions.PERMISSION.CAMERA
      );
      
      console.log('[CameraPermission] Android current permission:', hasPermission?.hasPermission);

      if (hasPermission?.hasPermission) {
        return true;
      }

      if (!shouldRequest) {
        return false;
      }

      console.log('[CameraPermission] Android: Requesting permission...');
      this.isRequestingPermission = true;

      const result = await this.androidPermissions.requestPermission(
        this.androidPermissions.PERMISSION.CAMERA
      );
      
      this.isRequestingPermission = false;

      const granted = result?.hasPermission || false;
      console.log('[CameraPermission] Android permission result:', granted);
      
      // Android uses NATIVE permissions only
      this.recordPermissionAttempt(granted, 'NATIVE');

      return granted;

    } catch (error) {
      console.error('[CameraPermission] Android permission error:', error);
      this.isRequestingPermission = false;
      return false;
    }
  }

  // ==================== WEB PERMISSION ====================

  private async checkWebCameraPermission(shouldRequest: boolean): Promise<boolean> {
    console.log('[CameraPermission] Web check, shouldRequest:', shouldRequest);
    
    try {
      if (!shouldRequest) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(d => d.kind === 'videoinput');
        return videoDevices.length > 0 && !!videoDevices[0].label;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      this.stopStream(stream);
      
      console.log('[CameraPermission] Web: Permission GRANTED');
      // Web uses BROWSER permissions
      this.recordPermissionAttempt(true, 'BROWSER');
      return true;
    } catch (error: any) {
      console.log('[CameraPermission] Web: Permission error:', error.name);
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        // Web uses BROWSER permissions
        this.recordPermissionAttempt(false, 'BROWSER');
      }
      
      return false;
    }
  }

  // ==================== OTHER METHODS ====================

  async checkMediaPermission(shouldRequest: boolean = false): Promise<boolean> {
    if (!this.isAndroid || !this.isCordova) {
      return true;
    }

    const { parsedVersion } = await this.getAndroidVersion();
    if (parsedVersion < 13) {
      return true;
    }

    try {
      const mediaPermissions = [
        'android.permission.READ_MEDIA_IMAGES',
        'android.permission.READ_MEDIA_VIDEO'
      ];

      let allGranted = true;
      for (const perm of mediaPermissions) {
        try {
          const result = await this.androidPermissions.checkPermission(perm);
          
          if (!result || !result.hasPermission) {
            allGranted = false;
            break;
          }
        } catch (checkError) {
          allGranted = false;
          break;
        }
      }

      if (allGranted) {
        return true;
      }

      if (!shouldRequest) {
        return false;
      }

      try {
        const requestResult = await this.androidPermissions.requestPermissions(mediaPermissions);
        return requestResult?.hasPermission || false;
      } catch (requestError) {
        console.error('[CameraPermission] Error requesting media permissions:', requestError);
        return false;
      }
    } catch (error) {
      console.error('[CameraPermission] Media permission error:', error);
      return true;
    }
  }

  async checkAndRequestAll(shouldRequest: boolean = false): Promise<PermissionCheckResult> {
    try {
      const camera = await this.checkCameraPermission(shouldRequest);
      return { camera };
    } catch (error) {
      console.error('[CameraPermission] checkAndRequestAll error:', error);
      return { camera: false };
    }
  }

  private async getAndroidVersion(): Promise<{ fullVersion: string; parsedVersion: number }> {
    try {
      const fullVersion = this.device.version || '0';
      const parsedVersion = parseInt(fullVersion.split('.')[0], 10) || 0;
      
      return { fullVersion, parsedVersion };
    } catch (error) {
      console.error('[CameraPermission] Error getting Android version:', error);
      return { fullVersion: 'Unknown', parsedVersion: 0 };
    }
  }

  async getBestCameraDevice(): Promise<string | null> {
    try {
      try {
        const testStream = await navigator.mediaDevices.getUserMedia({ video: true });
        this.stopStream(testStream);
      } catch {}

      const devices = (await navigator.mediaDevices.enumerateDevices())
        .filter(d => d.kind === 'videoinput');

      if (devices.length === 0) return null;
      if (devices.length === 1) return devices[0]?.deviceId || null;

      const backKeywords = ['back', 'rear', 'environment', 'facing back'];
      const frontKeywords = ['front', 'user', 'facing front'];

      for (const device of devices) {
        const label = (device.label || '').toLowerCase();
        const isBack = backKeywords.some(k => label.includes(k));
        const isFront = frontKeywords.some(k => label.includes(k));

        if (isBack && !isFront) {
          return device.deviceId || null;
        }
      }

      for (const device of devices) {
        if (!device.deviceId) continue;

        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { deviceId: { exact: device.deviceId }, facingMode: { ideal: 'environment' } }
          });

          const track = stream.getVideoTracks()[0];
          if (!track) {
            this.stopStream(stream);
            continue;
          }
          
          const settings = track.getSettings() as any;
          this.stopStream(stream);

          if (settings?.facingMode === 'environment') {
            return device.deviceId;
          }
        } catch {
          continue;
        }
      }

      return devices[0]?.deviceId || null;

    } catch (error) {
      console.error('[CameraPermission] Get camera device error:', error);
      return null;
    }
  }

async showSettingsPopup(
  missing: 'CAMERA' | 'MEDIA' | 'BOTH' = 'CAMERA',
  isBrowserDenial: boolean = false
): Promise<'opened_settings' | 'cancelled'> {
  console.log('[CameraPermission] showSettingsPopup called with missing:', missing, 'isBrowserDenial:', isBrowserDenial);
  
  await this.delay(300);
  
  let heading = 'Permission Required';
  let message = '';
  let buttonText = 'OK';

  // Browser denial - simple message to try again
  if (isBrowserDenial) {
    heading = 'Camera Permission Denied';
    
    if (this.isIOS && this.isCordova) {
      message = 'Browser camera access was denied.\n' +
                'To enable camera access:\n' +
                '1. Tap "OK" below\n' +
                '2. Tap "Scan QR" again\n' +
                '3. When the browser popup appears, tap "Allow"\n' +
                'The camera permission popup will appear each time until you allow it.';
    } else if (this.isAndroid && this.isCordova) {
      message = 'Browser camera access was denied.\n\n' +
                'To enable camera access:\n' +
                '1. Tap "OK" below\n' +
                '2. Tap "Scan QR" again\n' +
                '3. When prompted, tap "Allow" for camera access\n' +
                'Note: The permission popup will keep appearing until you grant access.';
    } else {
      message = 'Browser camera access was denied.\n' +
                'To enable camera access:\n' +
                '1. Click "OK" below\n' +
                '2. Click "Scan QR" again\n' +
                '3. When the browser asks for permission, click "Allow"\n' +
                'Tip: Look for the camera icon in your browser\'s address bar.';
    }
    
    buttonText = 'OK';
  }
  // Native denial - show settings instructions
  else {
    if (missing === 'BOTH') {
      heading = 'Permissions Required';
      message = 'To scan QR codes, you must enable both Camera and Storage/Media access in your device settings.';
    } else if (missing === 'MEDIA') {
      heading = 'Storage Permission Required';
      message = 'Storage/Media access is required for the QR scanner. Please enable it in your device settings.';
    } else {
      heading = 'Camera Permission Required';
      message = 'Camera access is required to scan QR codes and must be enabled in your device settings.';
    }

    if (this.isCordova && this.isIOS) {
      message += '\nIOS Instructions:\n' +
                 '1. Tap "Open Settings" below\n' +
                 '2. Find this app in Settings\n' +
                 '3. Tap on Camera\n' +
                 '4. Select "Allow"\n' +
                 '5. Return to this app\n' +
                 '6. Tap "Scan QR" again';
      buttonText = 'Open Settings';
    }
    else if (this.isCordova && this.isAndroid) {
      try {
        const { parsedVersion } = await this.getAndroidVersion();
        
        message += '\nAndroid Instructions:\n' +
                   '1. Tap "Open Settings" below\n';
        
        if (parsedVersion >= 15) {
          message += '2. Navigate to: Apps > App permissions > Camera\n';
        } else if (parsedVersion >= 13) {
          message += '2. Navigate to: Apps > Permissions > Camera\n';
        } else if (parsedVersion >= 11) {
          message += '2. Navigate to: Apps > Permissions > Camera\n';
        } else if (parsedVersion >= 10) {
          message += '2. Navigate to: Apps & notifications > App permissions\n';
        } else if (parsedVersion >= 6) {
          message += '2. Navigate to: Apps > App permissions\n';
        } else {
          message += '2. Find camera permissions\n';
        }
        
        message += '3. Enable camera access for this app\n' +
                   '4. Return to the app\n' +
                   '5. Tap "Scan QR" again';
      } catch (e) {
        message += '\n\nInstructions:\n\n' +
                   '1. Tap "Open Settings" below\n' +
                   '2. Enable camera permissions for this app\n' +
                   '3. Return to the app\n' +
                   '4. Tap "Scan QR" again';
      }
      buttonText = 'Open Settings';
    }
    else {
      message += '\n\nBrowser Instructions:\n\n' +
                 'Click the camera icon in your browser\'s address bar and select "Allow" for this site.\n\n' +
                 'After enabling, tap "Scan QR" again.';
      buttonText = 'OK';
    }
  }

  console.log('[CameraPermission] About to call popupService.showModal');
  
  return new Promise<'opened_settings' | 'cancelled'>((resolve) => {
    setTimeout(() => {
      this.ngZone.run(() => {
        this.popupService.showModal(
          {
            heading,
            message,
            btn: buttonText,
            rejectBtnText: 'Cancel',
            navigateRoute: '',
            imgURL: 'assets/images/error.png',
            showDismissButton: true,
            alignStart: true,
          },
          async () => {
            console.log('[CameraPermission] User clicked button:', buttonText);
            
            // Only open device settings for native denials on Cordova
            if (this.isCordova && !isBrowserDenial) {
              try {
                console.log('[CameraPermission] Opening device settings...');
                await this.diagnostic.switchToSettings();
                console.log('[CameraPermission] Settings opened successfully');
              } catch (error) {
                console.error('[CameraPermission] Error opening settings:', error);
                // Fallback for older diagnostic plugin versions
                try {
                  if (this.isIOS) {
                    // iOS fallback - try using URL scheme
                    (window as any).open('app-settings:');
                  } else if (this.isAndroid) {
                    // Android fallback - try using intent
                    (window as any).open('intent://settings#Intent;action=android.settings.APPLICATION_DETAILS_SETTINGS;end');
                  }
                } catch (fallbackError) {
                  console.error('[CameraPermission] Fallback also failed:', fallbackError);
                }
              }
            }
            
            resolve('opened_settings');
          },
          () => {
            console.log('[CameraPermission] User cancelled');
            resolve('cancelled');
          }
        ).catch(error => {
          console.error('[CameraPermission] Error showing popup:', error);
          resolve('cancelled');
        });
      });
    }, 100);
  });
}

  // Add this method after getBrowserDenialCount()
  getMostRecentDenialType(): 'NATIVE' | 'BROWSER' | null {
    if (this.permissionAttempts.length === 0) {
      return null;
    }
    
    // Get the most recent denial (not a grant)
    const denials = this.permissionAttempts.filter(a => !a.wasGranted);
    if (denials.length === 0) {
      return null;
    }
    
    // Sort by timestamp descending and get the most recent
    const sortedDenials = denials.sort((a, b) => b.timestamp - a.timestamp);
    return sortedDenials[0].type;
  }

  // Add this method to check if native permission is currently denied
  async isNativePermissionDenied(): Promise<boolean> {
    if (!this.isCordova) {
      return false;
    }
    
    try {
      if (this.isIOS) {
        const status = await this.diagnostic.getCameraAuthorizationStatus();
        return status === this.diagnostic.permissionStatus.DENIED_ALWAYS ||
              status === this.diagnostic.permissionStatus.DENIED_ONCE;
      }
      
      if (this.isAndroid) {
        const hasPermission = await this.androidPermissions.checkPermission(
          this.androidPermissions.PERMISSION.CAMERA
        );
        return !hasPermission?.hasPermission;
      }
    } catch (error) {
      console.error('[CameraPermission] Error checking native permission:', error);
    }
    
    return false;
  }
  isMobileDevice(): boolean {
    return this.platform.is('mobile') || this.platform.is('cordova');
  }

  private stopStream(stream: MediaStream | null | undefined): void {
    if (!stream) return;
    try {
      stream.getTracks().forEach(track => {
        try {
          track.stop();
        } catch (e) {
          console.warn('[CameraPermission] Error stopping track:', e);
        }
      });
    } catch (error) {
      console.warn('[CameraPermission] Error stopping stream:', error);
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}